// Paysol offline sync engine — drains pending_sales queue when the network is up.
// Fires:
//   - once at app boot (from main.js)
//   - on every 'online' window event
//   - every 30s while any pending sales exist
//
// A tiny event bus emits 'paysol-sync-changed' so any component can subscribe to counts / results.

import { listPending, markSyncing, markFailed, removePending, countPending } from './db';
import { paysol, execute } from '@/api';

let _draining = false;
let _pollHandle = null;

function emit(detail) {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('paysol-sync-changed', { detail }));
  }
}

export async function refreshCount() {
  const n = await countPending().catch(() => 0);
  emit({ type: 'count', pending: n });
  return n;
}

export async function drainQueue({ force = false } = {}) {
  if (_draining) return { started: false, reason: 'already draining' };
  if (!force && typeof navigator !== 'undefined' && !navigator.onLine) return { started: false, reason: 'offline' };
  _draining = true;
  emit({ type: 'draining' });

  let synced = 0, failed = 0;
  try {
    const all = await listPending();
    const pending = all.filter((r) => r.sync_state === 'pending');
    for (const row of pending) {
      try {
        await markSyncing(row.id);
        const { data } = await execute(row.payload, paysol);
        if (data && data.success) {
          await removePending(row.id);
          synced++;
          emit({ type: 'synced', row, serverReceipt: data.data && data.data.receiptNo });
        } else {
          await markFailed(row.id, (data && data.message) || 'Unknown API error');
          failed++;
        }
      } catch (e) {
        await markFailed(row.id, (e.response && e.response.data && e.response.data.message) || e.message || 'Network error');
        failed++;
      }
    }
  } finally {
    _draining = false;
  }
  const remaining = await refreshCount();
  emit({ type: 'done', synced, failed, remaining });
  return { synced, failed, remaining };
}

export function startAutoSync() {
  if (typeof window === 'undefined') return;
  // Kick immediately in case we already have queued items
  drainQueue();
  // Sync whenever we come back online
  window.addEventListener('online', () => drainQueue({ force: true }));
  // Poll every 30s while we have pending items
  if (_pollHandle) clearInterval(_pollHandle);
  _pollHandle = setInterval(async () => {
    const n = await countPending().catch(() => 0);
    if (n > 0 && navigator.onLine) drainQueue();
  }, 30000);
}
